import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { OwnerFormSection } from "@/components/owners/OwnerFormSection";
import { Button } from "@akuna/ui";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";

import { useOwner, useUpdateOwner } from "@/hooks/useOwners";
import {
  useDocumentTypes,
  useGenders,
  useLeadSources,
  useStates,
  type City,
  type DocumentType,
} from "@/hooks/useLookups";
import { supabase } from "@/integrations/supabase/client";
import { formatISOToDisplay } from "@/lib/date-utils";
import type { OwnerInput } from "@/lib/validations/owner";
import { ownerWizardSchema } from "@/lib/validations/ownerWizard";
import {
  applyDocumentMask,
  removeDocumentMask,
  validateDocument,
} from "@/lib/utils/document-masks";
import { CepLookupError, lookupCep, mapCepErrorToMessage } from "@/lib/cep";

type QuickEditOwnerDialogProps = {
  ownerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
};

export function QuickEditOwnerDialog({
  ownerId,
  open,
  onOpenChange,
  onSaved,
}: QuickEditOwnerDialogProps) {
  const { t } = useTranslation("medicalRecords");
  const { data: owner, isLoading } = useOwner(ownerId);
  const updateOwner = useUpdateOwner();

  const { data: genders } = useGenders();
  const { data: documentTypes } = useDocumentTypes();
  const { data: leadSources } = useLeadSources();
  const { data: states } = useStates();

  const [birthDateInput, setBirthDateInput] = useState("");
  const [isBirthDateCalendarOpen, setIsBirthDateCalendarOpen] = useState(false);
  const [documentInput, setDocumentInput] = useState("");

  const [addressStates, setAddressStates] = useState<(string | null)[]>([]);
  const [addressCities, setAddressCities] = useState<Record<number, City[]>>({});
  const [addressCepLoading, setAddressCepLoading] = useState<Record<number, boolean>>({});
  const [cityNameCache, setCityNameCache] = useState<Record<string, string>>({});
  const [cityDisplayValues, setCityDisplayValues] = useState<Record<number, string>>({});

  const form = useForm<OwnerInput>({
    resolver: zodResolver(ownerWizardSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      name: "",
      gender_id: "",
      document_type_id: "",
      document: "",
      birth_date: "",
      profession: "",
      marketing_consent: true,
      lead_source_id: "",
      phones: [{ phone_number: "", is_whatsapp: true, is_primary: true }],
      emails: [{ email: "", is_primary: true }],
      addresses: [
        {
          zip_code: "",
          street: "",
          number: "",
          complement: "",
          neighborhood: "",
          city_id: "",
          is_primary: true,
        },
      ],
    },
  });

  const documentTypeId = form.watch("document_type_id");
  const selectedDocumentType = useMemo(
    () =>
      documentTypes?.find((type) => type.id === documentTypeId) as
        | DocumentType
        | undefined,
    [documentTypes, documentTypeId],
  );

  const phones = form.watch("phones");
  const emails = form.watch("emails");
  const addresses = form.watch("addresses");

  // Reset local state when dialog closes so re-opens start fresh.
  useEffect(() => {
    if (!open) {
      setBirthDateInput("");
      setDocumentInput("");
      setAddressStates([]);
      setAddressCities({});
      setAddressCepLoading({});
      setCityNameCache({});
      setCityDisplayValues({});
    }
  }, [open]);

  const fetchCitiesForState = useCallback(
    async (stateId: string, index: number, cityNameToSelect?: string) => {
      if (!stateId) {
        setAddressCities((prev) => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });
        form.setValue(`addresses.${index}.city_id`, "", { shouldDirty: true });
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error: citiesError } = await (supabase as any)
        .from("cities")
        .select("id, name, state_id")
        .eq("state_id", stateId)
        .order("name");
      if (citiesError || !data) return;
      const typedData = data as City[];
      setAddressCities((prev) => ({ ...prev, [index]: typedData }));
      setCityNameCache((prev) => {
        const updated = { ...prev };
        typedData.forEach((city) => {
          updated[city.id] = city.name;
        });
        return updated;
      });
      if (cityNameToSelect) {
        const match = typedData.find(
          (city) =>
            city.name.toLowerCase() === cityNameToSelect.toLowerCase(),
        );
        if (match) {
          form.setValue(`addresses.${index}.city_id`, match.id, {
            shouldDirty: true,
          });
        }
      }
    },
    [form],
  );

  const handleAddAddress = useCallback(() => {
    const currentAddresses = form.getValues("addresses") || [];
    form.setValue(
      "addresses",
      [
        ...currentAddresses,
        {
          zip_code: "",
          street: "",
          number: "",
          complement: "",
          neighborhood: "",
          city_id: "",
          is_primary: false,
        },
      ],
      { shouldDirty: true },
    );
    setAddressStates((prev) => [...prev, null]);
  }, [form]);

  const handleRemoveAddress = useCallback(
    (index: number) => {
      const currentAddresses = form.getValues("addresses") || [];
      const updatedAddresses = currentAddresses.filter((_, i) => i !== index);
      form.setValue("addresses", updatedAddresses, { shouldDirty: true });
      setAddressStates((prev) => prev.filter((_, i) => i !== index));
      setAddressCities((prev) => {
        const reordered: Record<number, City[]> = {};
        updatedAddresses.forEach((_, newIndex) => {
          const orig = newIndex >= index ? newIndex + 1 : newIndex;
          if (Object.prototype.hasOwnProperty.call(prev, orig))
            reordered[newIndex] = prev[orig];
        });
        return reordered;
      });
      setAddressCepLoading((prev) => {
        const reordered: Record<number, boolean> = {};
        updatedAddresses.forEach((_, newIndex) => {
          const orig = newIndex >= index ? newIndex + 1 : newIndex;
          if (Object.prototype.hasOwnProperty.call(prev, orig))
            reordered[newIndex] = prev[orig];
        });
        return reordered;
      });
      setCityDisplayValues((prev) => {
        const reordered: Record<number, string> = {};
        updatedAddresses.forEach((_, newIndex) => {
          const orig = newIndex >= index ? newIndex + 1 : newIndex;
          if (Object.prototype.hasOwnProperty.call(prev, orig))
            reordered[newIndex] = prev[orig];
        });
        return reordered;
      });
    },
    [form],
  );

  const handleCepLookup = useCallback(
    async (index: number) => {
      const cepValue =
        (form.getValues(`addresses.${index}.zip_code`) as string) ?? "";
      const cepDigits = cepValue.replace(/\D/g, "");
      if (cepDigits.length !== 8) {
        form.setError(`addresses.${index}.zip_code`, {
          type: "manual",
          message: "Informe um CEP válido (8 dígitos)",
        });
        return;
      }
      form.setValue(`addresses.${index}.street`, "", { shouldDirty: true });
      form.setValue(`addresses.${index}.number`, "", { shouldDirty: true });
      form.setValue(`addresses.${index}.complement`, "", { shouldDirty: true });
      form.setValue(`addresses.${index}.neighborhood`, "", {
        shouldDirty: true,
      });
      form.setValue(`addresses.${index}.city_id`, "", { shouldDirty: true });
      form.clearErrors(`addresses.${index}.city_id`);
      setAddressStates((prev) => {
        const u = [...prev];
        u[index] = null;
        return u;
      });
      setAddressCities((prev) => {
        const u = { ...prev };
        delete u[index];
        return u;
      });
      setCityDisplayValues((prev) => ({ ...prev, [index]: "" }));
      form.clearErrors(`addresses.${index}.zip_code`);
      try {
        setAddressCepLoading((prev) => ({ ...prev, [index]: true }));
        const data = await lookupCep(cepDigits);
        form.clearErrors(`addresses.${index}.zip_code`);
        if (data.street)
          form.setValue(`addresses.${index}.street`, data.street, {
            shouldDirty: true,
          });
        if (data.neighborhood)
          form.setValue(
            `addresses.${index}.neighborhood`,
            data.neighborhood,
            { shouldDirty: true },
          );
        if (data.complement)
          form.setValue(`addresses.${index}.complement`, data.complement, {
            shouldDirty: true,
          });
        const stateMatch = states?.find((state) => state.code === data.state);
        if (stateMatch) {
          setAddressStates((prev) => {
            const u = [...prev];
            u[index] = stateMatch.id;
            return u;
          });
          await fetchCitiesForState(stateMatch.id, index, data.city);
        }
      } catch (lookupError) {
        const code =
          lookupError instanceof CepLookupError
            ? lookupError.code
            : "LOOKUP_FAILED";
        form.setError(`addresses.${index}.zip_code`, {
          type: "manual",
          message: mapCepErrorToMessage(code),
        });
      } finally {
        setAddressCepLoading((prev) => ({ ...prev, [index]: false }));
      }
    },
    [fetchCitiesForState, form, states],
  );

  // Keep document input masked.
  useEffect(() => {
    setDocumentInput((prev) => {
      const masked = applyDocumentMask(
        prev || form.getValues("document") || "",
        selectedDocumentType,
      );
      if (masked !== prev) {
        form.setValue("document", masked, { shouldValidate: true });
      }
      return masked;
    });
  }, [form, selectedDocumentType]);

  // Default whatsapp + primary phone normalization.
  useEffect(() => {
    if (!phones) return;
    let updated = false;
    const normalized = (phones ?? []).map((phone, index) => {
      const next = { ...phone };
      if (next.is_whatsapp === undefined) {
        next.is_whatsapp = true;
        updated = true;
      }
      const shouldBePrimary = index === 0;
      if (next.is_primary !== shouldBePrimary) {
        next.is_primary = shouldBePrimary;
        updated = true;
      }
      return next;
    });
    if (updated) {
      form.setValue("phones", normalized, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [phones, form]);

  // Enforce 1 email only.
  useEffect(() => {
    const current = form.getValues("emails") ?? [];
    if (current.length === 0) {
      form.setValue(
        "emails",
        [{ email: "", is_primary: true }],
        { shouldDirty: false, shouldValidate: false },
      );
      return;
    }
    if (current.length > 1) {
      form.setValue("emails", [current[0]], {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }
    if (current[0] && current[0].is_primary !== true) {
      form.setValue("emails.0.is_primary", true, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [emails, form]);

  // Sync addressStates length with addresses length.
  useEffect(() => {
    if (!addresses || addresses.length === 0) {
      setAddressStates([]);
      return;
    }
    setAddressStates((prev) => {
      const next = addresses.map((_, index) => prev[index] ?? null);
      if (
        next.length === prev.length &&
        next.every((v, i) => v === prev[i])
      )
        return prev;
      return next;
    });
  }, [addresses]);

  // Normalize primary address.
  useEffect(() => {
    if (!addresses) return;
    let updated = false;
    const normalized = addresses.map((address, index) => {
      const shouldBePrimary = index === 0;
      if (address.is_primary !== shouldBePrimary) {
        updated = true;
        return { ...address, is_primary: shouldBePrimary };
      }
      return address;
    });
    if (updated) {
      form.setValue("addresses", normalized, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [addresses, form]);

  // Fetch city lists for selected states.
  useEffect(() => {
    addressStates.forEach((stateId, index) => {
      if (stateId) {
        fetchCitiesForState(stateId, index).catch(() => {});
      }
    });
  }, [addressStates, fetchCitiesForState]);

  // Sync city display values.
  useEffect(() => {
    (addresses ?? []).forEach((_, index) => {
      const cityId = form.watch(`addresses.${index}.city_id`);
      const citiesForState = addressCities[index] ?? [];
      const selectedCity = citiesForState.find((city) => city.id === cityId);
      if (selectedCity && !cityDisplayValues[index]) {
        setCityDisplayValues((prev) => ({
          ...prev,
          [index]: selectedCity.name,
        }));
      } else if (cityId && cityNameCache[cityId] && !cityDisplayValues[index]) {
        setCityDisplayValues((prev) => ({
          ...prev,
          [index]: cityNameCache[cityId],
        }));
      }
    });
  }, [addresses, addressCities, cityNameCache, cityDisplayValues, form]);

  // Load owner data into form when dialog opens.
  useEffect(() => {
    if (!owner || !open) return;

    const normalizedPhones = owner.phones?.length
      ? owner.phones.map((phone) => ({
          id: phone.id ?? undefined,
          phone_number: phone.phone_number ?? "",
          is_whatsapp: Boolean(phone.is_whatsapp),
          is_primary: Boolean(phone.is_primary),
        }))
      : [{ phone_number: "", is_whatsapp: true, is_primary: true }];

    const normalizedEmails = owner.emails?.length
      ? [
          {
            id: owner.emails[0]?.id ?? undefined,
            email: owner.emails[0]?.email ?? "",
            is_primary: true,
          },
        ]
      : [{ email: "", is_primary: true }];

    const cityCacheUpdates: Record<string, string> = {};
    const normalizedAddresses = owner.addresses?.length
      ? owner.addresses.map((address, index) => {
          const cityId = address.city_id ?? "";
          if (cityId && address.city?.name)
            cityCacheUpdates[cityId] = address.city.name;
          return {
            id: address.id ?? undefined,
            zip_code: address.zip_code ?? "",
            street: address.street ?? "",
            number: address.number ?? "",
            complement: address.complement ?? "",
            neighborhood: address.neighborhood ?? "",
            city_id: cityId,
            is_primary: index === 0,
          };
        })
      : [
          {
            zip_code: "",
            street: "",
            number: "",
            complement: "",
            neighborhood: "",
            city_id: "",
            is_primary: true,
          },
        ];

    if (Object.keys(cityCacheUpdates).length > 0) {
      setCityNameCache((prev) => ({ ...prev, ...cityCacheUpdates }));
    }

    form.reset({
      name: owner.name ?? "",
      gender_id: owner.gender_id ?? "",
      document_type_id: owner.document_type_id ?? "",
      document: owner.document ?? "",
      birth_date: owner.birth_date ?? "",
      profession: owner.profession ?? "",
      marketing_consent: owner.marketing_consent ?? true,
      lead_source_id: owner.lead_source_id ?? "",
      phones: normalizedPhones,
      emails: normalizedEmails,
      addresses: normalizedAddresses,
    });

    setBirthDateInput(formatISOToDisplay(owner.birth_date ?? ""));
    setDocumentInput(owner.document ?? "");
    setCityDisplayValues({});
    setAddressStates(
      normalizedAddresses.map(
        (_, index) => owner.addresses?.[index]?.city?.state_id ?? null,
      ),
    );
  }, [owner, open, form]);

  const onSubmit = async (data: OwnerInput) => {
    const validation = validateDocument(documentInput, selectedDocumentType);
    if (!validation.isValid) {
      form.setError("document", {
        type: "manual",
        message: validation.error,
      });
      return;
    }

    const currentAddresses = form.getValues("addresses") ?? [];
    const invalidCities = currentAddresses
      .map((address, index) => {
        const cityId = address.city_id;
        const displayValue = cityDisplayValues[index];
        if (displayValue && displayValue.trim() && !cityId) return index;
        return null;
      })
      .filter((value): value is number => value !== null);

    if (invalidCities.length) {
      invalidCities.forEach((index) => {
        form.setError(`addresses.${index}.city_id`, {
          type: "manual",
          message: "Selecione uma cidade da lista",
        });
      });
      return;
    }

    const sanitizedPhones = (data.phones ?? [])
      .map((phone) => ({
        ...phone,
        phone_number: (phone.phone_number ?? "").trim(),
      }))
      .filter((phone) => Boolean(phone.phone_number))
      .map((phone, index) => ({ ...phone, is_primary: index === 0 }));

    const sanitizedEmails = (data.emails ?? [])
      .map((email) => ({ ...email, email: (email.email ?? "").trim() }))
      .filter((email) => Boolean(email.email))
      .slice(0, 1)
      .map((email, index) => ({ ...email, is_primary: index === 0 }));

    const sanitizedDocument = (data.document ?? "").trim();
    const persistedDocument = sanitizedDocument
      ? removeDocumentMask(sanitizedDocument, selectedDocumentType)
      : "";
    const sanitizedDocumentTypeId = sanitizedDocument
      ? (data.document_type_id ?? "").trim()
      : "";

    const sanitizedAddresses = (data.addresses ?? [])
      .map((address) => ({
        ...address,
        zip_code: (address.zip_code ?? "").trim(),
        street: (address.street ?? "").trim(),
        number: (address.number ?? "").trim(),
        complement: (address.complement ?? "").trim(),
        neighborhood: (address.neighborhood ?? "").trim(),
        city_id: (address.city_id ?? "").trim(),
      }))
      .filter((address) =>
        Boolean(
          address.zip_code ||
            address.street ||
            address.number ||
            address.complement ||
            address.neighborhood ||
            address.city_id,
        ),
      )
      .map((address, index) => ({ ...address, is_primary: index === 0 }));

    const payload: OwnerInput = {
      ...data,
      document: persistedDocument,
      document_type_id: sanitizedDocumentTypeId,
      phones: sanitizedPhones,
      emails: sanitizedEmails,
      addresses: sanitizedAddresses,
    };

    await updateOwner.mutateAsync({ id: ownerId, input: payload });
    toast.success(t("quickEdit.ownerDialog.successToast"));
    onSaved?.();
    onOpenChange(false);
  };

  const isSaving = updateOwner.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent noAnimation className="max-h-[90vh] max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("quickEdit.ownerDialog.title")}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <OwnerFormSection
                form={form}
                genders={genders}
                documentTypes={documentTypes}
                leadSources={leadSources}
                states={states}
                birthDateInput={birthDateInput}
                setBirthDateInput={setBirthDateInput}
                isBirthDateCalendarOpen={isBirthDateCalendarOpen}
                setIsBirthDateCalendarOpen={setIsBirthDateCalendarOpen}
                documentInput={documentInput}
                setDocumentInput={setDocumentInput}
                phones={phones}
                addresses={addresses}
                addressStates={addressStates}
                setAddressStates={setAddressStates}
                addressCities={addressCities}
                addressCepLoading={addressCepLoading}
                cityNameCache={cityNameCache}
                setCityNameCache={setCityNameCache}
                cityDisplayValues={cityDisplayValues}
                setCityDisplayValues={setCityDisplayValues}
                fetchCitiesForState={fetchCitiesForState}
                handleAddAddress={handleAddAddress}
                handleRemoveAddress={handleRemoveAddress}
                handleCepLookup={handleCepLookup}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSaving}
                >
                  {t("quickEdit.ownerDialog.cancelButton")}
                </Button>
                <Button type="submit" disabled={isSaving}>
                  {isSaving
                    ? t("quickEdit.ownerDialog.savingButton")
                    : t("quickEdit.ownerDialog.saveButton")}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
